<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';
import WhatsAppCampaignDetails from './WhatsAppCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  // Campanha de WhatsApp: o card passa a mostrar template, audiência e o que de fato saiu.
  isWhatsAppType: {
    type: Boolean,
    default: false,
  },
  audience: {
    type: String,
    default: '',
  },
  templateName: {
    type: String,
    default: '',
  },
  dispatch: {
    type: Object,
    default: null,
  },
  // O que a META respondeu. Diferente de `dispatch`, que conta o que o backend aceitou.
  delivery: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['edit', 'delete', 'open']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

// Agendada é a campanha que ainda não saiu e tem hora marcada no futuro. `campaign_status` sozinho
// não distingue: uma campanha esperando o cron e outra disparando agora são as duas `active`.
const isScheduled = computed(
  () =>
    !props.isLiveChatType &&
    props.status !== STATUS_COMPLETED &&
    !props.dispatch?.running &&
    props.scheduledAt * 1000 > Date.now()
);

// Falhou tanto quando o lote morreu quanto quando a Meta recusou TODAS as mensagens: nos dois
// casos ninguém recebeu, e chamar isso de "concluída" é o defeito que esta tela tinha.
const hasFailed = computed(
  () =>
    Boolean(props.dispatch?.error) ||
    ((props.delivery?.failed ?? 0) > 0 &&
      (props.delivery?.delivered ?? 0) + (props.delivery?.read ?? 0) === 0)
);

const statusTextColor = computed(() => ({
  'text-n-ruby-11': hasFailed.value,
  'text-n-amber-11': !hasFailed.value && isScheduled.value,
  'text-n-teal-11': !hasFailed.value && !isScheduled.value && isActive.value,
  'text-n-slate-12': !hasFailed.value && !isScheduled.value && !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (hasFailed.value) return t('CAMPAIGN.WHATSAPP.CARD.STATUS.FAILED');
  if (props.dispatch?.running) {
    return t('CAMPAIGN.WHATSAPP.CARD.STATUS.RUNNING');
  }
  if (isScheduled.value) return t('CAMPAIGN.WHATSAPP.CARD.STATUS.SCHEDULED');

  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.WHATSAPP.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.WHATSAPP.CARD.STATUS.SCHEDULED');
});

// O corpo. Na campanha por template não existe texto livre: o modelo exige `message` e o disparo
// grava `[template] <nome>` só para satisfazer a validação. Mostrar isso como se fosse a mensagem
// do cliente é o que deixa a lista ilegível — então quando há template, quem manda é o nome dele.
const bodyText = computed(() =>
  props.templateName
    ? t('CAMPAIGN.WHATSAPP.CARD.TEMPLATE', { name: props.templateName })
    : props.message
);

const hasOutcome = computed(
  () => props.isWhatsAppType && (props.dispatch?.total ?? 0) > 0
);

// A Meta respondeu sobre alguma mensagem desta campanha? Enquanto não respondeu, o único número
// honesto é o do lote ("entregue ao backend"). Depois que responde, o que vale é o dela.
const temResposta = computed(() => (props.delivery?.total ?? 0) > 0);
const falharam = computed(() => props.delivery?.failed ?? 0);
const entregues = computed(
  () => (props.delivery?.delivered ?? 0) + (props.delivery?.read ?? 0)
);
const aguardando = computed(
  () => (props.delivery?.sent ?? 0) + (props.delivery?.progress ?? 0)
);

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div v-if="templateName" class="h-6 text-sm truncate text-n-slate-11">
        {{ bodyText }}
      </div>
      <div
        v-else
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <WhatsAppCampaignDetails
          v-else-if="isWhatsAppType"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :audience="audience"
          :scheduled-at="scheduledAt"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>

      <!-- O que de fato saiu. Só aparece quando houve disparo: numa campanha agendada, zeros
           dariam a impressão de que ela já rodou e falhou com todo mundo. -->
      <div
        v-if="hasOutcome"
        class="flex items-center gap-3 pt-2 mt-1 border-t border-n-weak w-full"
      >
        <!-- Enquanto a Meta não respondeu, o único número honesto é o do lote. Depois, o que
             vale é o dela: uma campanha com lote "1 enviado, 0 erros" pode ter a única mensagem
             recusada, e era isso que a tela chamava de concluída. -->
        <span v-if="temResposta && entregues" class="text-sm text-n-teal-11">
          {{
            t(
              'CAMPAIGN.WHATSAPP.CARD.OUTCOME.DELIVERED',
              { count: entregues },
              entregues
            )
          }}
        </span>
        <span v-if="temResposta && aguardando" class="text-sm text-n-slate-11">
          {{
            t(
              'CAMPAIGN.WHATSAPP.CARD.OUTCOME.AWAITING',
              { count: aguardando },
              aguardando
            )
          }}
        </span>
        <span v-if="temResposta && falharam" class="text-sm text-n-ruby-11">
          {{
            t(
              'CAMPAIGN.WHATSAPP.CARD.OUTCOME.REJECTED',
              { count: falharam },
              falharam
            )
          }}
        </span>
        <span v-if="!temResposta" class="text-sm text-n-teal-11">
          {{
            t(
              'CAMPAIGN.WHATSAPP.CARD.OUTCOME.SENT',
              { count: dispatch.sent },
              dispatch.sent
            )
          }}
        </span>
        <span v-if="dispatch.failed" class="text-sm text-n-ruby-11">
          {{
            t(
              'CAMPAIGN.WHATSAPP.CARD.OUTCOME.FAILED',
              { count: dispatch.failed },
              dispatch.failed
            )
          }}
        </span>
        <span v-if="dispatch.skipped" class="text-sm text-n-slate-11">
          {{
            t(
              'CAMPAIGN.WHATSAPP.CARD.OUTCOME.SKIPPED',
              { count: dispatch.skipped },
              dispatch.skipped
            )
          }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.CARD.OUTCOME.OF_TOTAL', {
              count: dispatch.total,
            })
          }}
        </span>
      </div>
      <p v-if="hasFailed" class="w-full text-sm truncate text-n-ruby-11">
        {{ dispatch.error }}
      </p>
    </div>
    <div class="flex items-center justify-end w-28 gap-2">
      <!-- Ver o detalhe é a ação principal do card: é lá que se descobre QUEM não recebeu. -->
      <Button
        v-if="isWhatsAppType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-list-checks"
        @click="emit('open')"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
